import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle2, KeyRound, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Used by both partner-onboarding welcome emails AND the standard
 * forgot-password flow. URL: /set-password?token=...
 */
export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const token = params.get('token') || '';
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error('This link is missing its token. Request a new email.');
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (pwd.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (pwd !== confirm) { toast.error('Passwords don\'t match'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/set-password`, { token, password: pwd });
      // Auto-sign-in: backend returned a fresh JWT for this user
      setSession(res.data.token, res.data.user);
      setDone(true);
      toast.success('Password set — you\'re signed in!');
      setTimeout(() => {
        const role = res.data.user?.role;
        if (role === 'vendor') navigate('/vendor');
        else if (role === 'admin') navigate('/admin/dashboard');
        else navigate('/dashboard');
      }, 1200);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Could not set password');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-md" data-testid="set-password-card">
        <CardHeader className="space-y-1 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center mx-auto mb-2">
            <KeyRound className="w-7 h-7 text-white" />
          </div>
          <CardTitle className="text-2xl">Set your password</CardTitle>
          <CardDescription>Pick a strong password to access your Eventism account.</CardDescription>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center py-6 space-y-3" data-testid="set-password-done">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              <p className="font-semibold">All set — signing you in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="pwd">New password</Label>
                <div className="relative">
                  <Input
                    id="pwd"
                    type={show ? 'text' : 'password'}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    autoFocus
                    data-testid="new-password-input"
                  />
                  <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-label="Toggle visibility">
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="confirm">Confirm password</Label>
                <Input
                  id="confirm"
                  type={show ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button type="submit" disabled={submitting || !pwd || pwd !== confirm} className="w-full bg-gradient-to-r from-indigo-600 to-fuchsia-600" data-testid="set-password-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Set password & continue
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Already have an account? <a href="/login" className="text-fuchsia-400 hover:underline">Sign in</a>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
